<template>
  <div :class="$style.taskList">
    <TaskBar
      v-for="task in tasks"
      :key="task.id"
      :task="task"
      :workday-ms="maxWorkdayMs"
      :is-new-task="task.id === lastCreatedTaskId"
      :view-mode="viewMode"
      :filter-period="selectedPeriod"
      :date-range="dateRange"
      :saturation="saturation"
      @toggle="$emit('toggleTask', task.id)"
      @delete="$emit('deleteTask', task.id)"
      @update:name="$emit('updateTaskName', task.id, $event)"
      @update:duration="$emit('updateTaskDuration', task.id, $event)"
      @update:estimate="$emit('updateTaskEstimate', task.id, $event)"
      @update:topic="$emit('updateTaskTopic', task.id, $event)"
      @update:project="$emit('updateTaskProject', task.id, $event)"
      @toggle:completed="$emit('toggleTaskCompleted', task.id)"
      @update:sessions="$emit('updateTaskSessions', task.id, $event)"
      @update:task="$emit('updateTask', task.id, $event)"
      @move-sessions="$emit('moveSessions', task.id, $event)"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import TaskBar from "../TaskBar.vue";
import { ViewMode } from "../../types/enums";

interface Props {
  tasks: any[];
  viewMode: ViewMode;
  selectedPeriod: string;
  dateRange: { start: string; end: string };
  saturation: number;
  lastCreatedTaskId: number | null;
}

interface Emits {
  (e: "toggleTask", taskId: number): void;
  (e: "deleteTask", taskId: number): void;
  (e: "updateTaskName", taskId: number, name: string): void;
  (e: "updateTaskDuration", taskId: number, duration: number): void;
  (e: "updateTaskEstimate", taskId: number, estimate: number): void;
  (e: "updateTaskTopic", taskId: number, topicId: string): void;
  (e: "updateTaskProject", taskId: number, projectId?: string): void;
  (e: "toggleTaskCompleted", taskId: number): void;
  (e: "updateTaskSessions", taskId: number, sessions: any[]): void;
  (e: "updateTask", taskId: number, task: any): void;
  (
    e: "moveSessions",
    taskId: number,
    data: {
      sessionIndices: number[];
      newTaskName: string;
      newTaskTopicId: string;
      newTaskEstimatedDuration: number;
    }
  ): void;
}

const props = defineProps<Props>();
defineEmits<Emits>();

const WORKDAY_MS = 120 * 1000;

// Calculate the maximum workday duration based on estimates
const maxWorkdayMs = computed(() => {
  const maxEstimate = Math.max(
    ...props.tasks.map((task) => task.estimatedDuration),
    WORKDAY_MS
  );
  return maxEstimate;
});
</script>

<style module>
.taskList {
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
}
</style>
